
import { supabase } from '@/integrations/supabase/client';
import { AssessmentResult } from '@/types/assessment';
import { toast } from 'sonner';
import { updatePdfUrl } from '@/utils/reports/reportStorageService';

const BUCKET_NAME = 'assessmentresultspdf';

/**
 * Uploads a PDF blob to Supabase storage
 */
export const uploadPdfToStorage = async (
  pdfBlob: Blob,
  userId: string,
  resultId: string
): Promise<string | null> => {
  try {
    if (!pdfBlob || pdfBlob.size < 1000) {
      return null;
    }

    // Generate a unique filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filePath = `${userId}/${resultId}_${timestamp}.pdf`;
    
    // Upload the file
    const { data, error } = await supabase
      .storage
      .from(BUCKET_NAME)
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        upsert: true,
      });
    
    if (error) {
      toast.error('Failed to save PDF to cloud storage');
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase
      .storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);
    
    // Update the PDF URL using the Edge Function
    if (resultId) {
      try {
        // Get current session token for authorization
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token || '';
        
        const response = await fetch(`https://obuhrpfphcqeqpvttcum.supabase.co/functions/v1/update-pdf-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            resultId,
            pdfUrl: publicUrl
          })
        });
        
        const result = await response.json();
        
        if (!result.success) {
          toast.error('Failed to update assessment result with PDF URL');
        }
      } catch (error) {
        toast.error('Error updating PDF URL');
      }
    }
    
    return publicUrl;
  } catch (error) {
    toast.error('Error saving PDF to cloud storage');
    return null;
  }
};

/**
 * Downloads a PDF from Supabase storage or generates it if not available
 */
export const downloadStoredPdf = async (
  result: AssessmentResult,
  generateIfNotFound: boolean = true
): Promise<boolean> => {
  try {
    // Check if we have a stored PDF URL
    if (result.pdfUrl) {
      // Open in new tab instead of downloading
      window.open(result.pdfUrl, '_blank');
      
      toast.success('PDF report opened in new tab');
      return true;
    } 
    
    // If no stored PDF and we're asked to generate it
    if (generateIfNotFound) {
      // Import the PDF generator dynamically to avoid circular dependencies
      const { downloadPDF } = await import('@/utils/pdf/pdfGenerator');
      return await downloadPDF(result);
    }
    
    toast.error('PDF not available');
    return false;
  } catch (error) {
    toast.error('Failed to download PDF');
    return false;
  }
};
